import axios from 'axios';
import { normalizePlace } from './placeNormalizer';

const API_KEY = process.env.REACT_APP_RAPID_API_TRAVEL_API_KEY;
const HOST = process.env.REACT_APP_RAPID_API_TRAVEL_LEGACY_HOST || 'travel-advisor.p.rapidapi.com';

const CONTENT_TYPE_MAP = {
  restaurants: 'restaurant',
  hotels: 'hotel',
  attractions: 'attraction',
};

const PLACETYPE_MAP = {
  restaurants: 'restaurant',
  hotels: 'hotel',
  attractions: 'attraction',
};

const toMinRatingValue = (rating) => {
  const value = Math.round((Number(rating) || 0) * 10);
  return value > 0 ? String(value) : '0';
};

const haversineKm = (a, b) => {
  if (!a || !b) return null;
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 6371 * 2 * Math.asin(Math.sqrt(h));
};

const dedupePlaces = (items) => {
  const seen = new Set();
  return items.filter((place) => {
    if (!place?.id || seen.has(place.id)) return false;
    seen.add(place.id);
    return true;
  });
};

const parseNumberFromReviewText = (value) => {
  if (!value) return 0;
  const cleaned = String(value).replace(/[^\d]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parsePriceLevel = (text) => {
  if (!text) return 'N/A';
  const match = text.match(/\$+/);
  return match ? match[0] : 'N/A';
};

const uniqueByLabel = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = (item?.label || '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const findSuggestionsInPayload = (payload) => {
  const suggestions = [];
  const visited = new Set();

  const visit = (node) => {
    if (!node || typeof node !== 'object') return;
    if (visited.has(node)) return;
    visited.add(node);

    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    const label = (
      (typeof node?.names?.name === 'string' && node.names.name)
      || (typeof node?.name === 'string' && node.name)
      || (typeof node?.title?.string === 'string' && node.title.string)
      || (typeof node?.displayName?.string === 'string' && node.displayName.string)
      || null
    );

    if (label) {
      const lat = node?.geoPoint?.latitude ?? node?.latitude ?? node?.lat;
      const lng = node?.geoPoint?.longitude ?? node?.longitude ?? node?.lng ?? node?.lon;
      suggestions.push({
        label,
        lat: typeof lat === 'number' ? lat : null,
        lng: typeof lng === 'number' ? lng : null,
      });
    }

    Object.values(node).forEach(visit);
  };

  visit(payload);
  return uniqueByLabel(suggestions);
};

const findFirstLatLng = (node, seen = new Set()) => {
  if (!node || typeof node !== 'object') return null;
  if (seen.has(node)) return null;
  seen.add(node);

  const lat = node.latitude ?? node.lat ?? node?.geoPoint?.latitude;
  const lng = node.longitude ?? node.lng ?? node.lon ?? node?.geoPoint?.longitude;
  if (typeof lat === 'number' && typeof lng === 'number') {
    return { lat, lng };
  }

  if (Array.isArray(node)) {
    for (const item of node) {
      const result = findFirstLatLng(item, seen);
      if (result) return result;
    }
    return null;
  }

  for (const value of Object.values(node)) {
    const result = findFirstLatLng(value, seen);
    if (result) return result;
  }

  return null;
};

export const resolveSearchLocation = async (query) => {
  const trimmed = (query || '').trim();
  if (!API_KEY || !trimmed) return null;

  try {
    const { data } = await axios.get(`https://${HOST}/locations/v2/auto-complete`, {
      params: {
        query: trimmed,
        lang: 'en_US',
        units: 'km',
      },
      headers: {
        'x-rapidapi-key': API_KEY,
        'x-rapidapi-host': HOST,
      },
    });

    return findFirstLatLng(data);
  } catch (error) {
    console.warn('locations/v2/auto-complete failed:', error?.response?.status || error?.message);
    return null;
  }
};

export const fetchLocationSuggestions = async (query) => {
  const trimmed = (query || '').trim();
  if (!API_KEY || trimmed.length < 2) return [];

  try {
    const { data } = await axios.get(`https://${HOST}/locations/v2/auto-complete`, {
      params: {
        query: trimmed,
        lang: 'en_US',
        units: 'km',
      },
      headers: {
        'x-rapidapi-key': API_KEY,
        'x-rapidapi-host': HOST,
      },
    });

    return findSuggestionsInPayload(data).slice(0, 8);
  } catch (error) {
    console.warn('fetchLocationSuggestions failed:', error?.response?.status || error?.message);
    return [];
  }
};

const parseNearbyResponse = (payload) => {
  const root = payload?.data?.AppPresentation_queryNearToALocation;
  if (!root) return [];

  const mapSections = root.mapSections || [];
  const dynamicMapSection = mapSections.find((section) => Array.isArray(section?.pins));
  const carouselSection = mapSections.find((section) => Array.isArray(section?.content));

  const pinMap = new Map();
  (dynamicMapSection?.pins || []).forEach((pin) => {
    const id = pin?.saveId?.id || pin?.trackingKey;
    const lat = pin?.geoPoint?.latitude;
    const lng = pin?.geoPoint?.longitude;
    if (!id || typeof lat !== 'number' || typeof lng !== 'number') return;
    pinMap.set(String(id), { lat, lng });
  });

  return (carouselSection?.content || []).map((card) => {
    const id = String(card?.saveId?.id || card?.cardLink?.route?.params?.contentId || '');
    const geo = pinMap.get(id);
    if (!id || !geo) return null;

    const title = card?.cardTitle?.string || 'Unknown place';
    const rating = Number(card?.bubbleRating?.rating) || 0;
    const reviews = parseNumberFromReviewText(card?.bubbleRating?.numberReviews?.string);
    const primaryInfo = card?.primaryInfo?.text || '';
    const secondaryInfo = card?.secondaryInfo?.text || '';
    const distanceText = card?.distance?.string || '';
    const category = card?.icon?.name || card?.icon?.activeName || '';
    const photoTemplate = card?.cardPhoto?.sizes?.urlTemplate;
    const photoUrl = photoTemplate
      ? photoTemplate.replace('{width}', '900').replace('{height}', '600')
      : undefined;

    return {
      location_id: id,
      name: title,
      latitude: geo.lat,
      longitude: geo.lng,
      rating,
      num_reviews: reviews,
      price_level: parsePriceLevel(primaryInfo),
      ranking: secondaryInfo || card?.distance?.string || 'Not ranked',
      summaryText: primaryInfo || '',
      statusText: secondaryInfo || '',
      distanceText,
      category,
      photo: photoUrl ? { images: { large: { url: photoUrl } } } : null,
      web_url: card?.cardLink?.route?.url || '',
      isOpenNow: secondaryInfo.toLowerCase().includes('open'),
    };
  }).filter(Boolean);
};

const looksLikePlace = (node) => {
  if (!node || typeof node !== 'object') return false;
  const hasName = typeof node.name === 'string' || typeof node.title === 'string';
  const hasLatLng = (
    node.latitude !== undefined
    || node.lat !== undefined
    || node?.coordinates?.latitude !== undefined
    || node?.coordinates?.lat !== undefined
  ) && (
    node.longitude !== undefined
    || node.lng !== undefined
    || node.lon !== undefined
    || node?.coordinates?.longitude !== undefined
    || node?.coordinates?.lng !== undefined
  );

  return hasName && hasLatLng;
};

const deepExtractPlaces = (payload) => {
  const collected = [];
  const seen = new Set();

  const visit = (node) => {
    if (!node || typeof node !== 'object') return;
    if (seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    if (looksLikePlace(node)) {
      collected.push(node);
    }

    Object.values(node).forEach(visit);
  };

  visit(payload);
  return collected;
};

const headers = {
  'x-rapidapi-key': API_KEY,
  'x-rapidapi-host': HOST,
  'Content-Type': 'application/json',
};

const formatDate = (date) => date.toISOString().slice(0, 10);

const getDateOffsets = () => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() + 1);
  const end = new Date(now);
  end.setDate(now.getDate() + 2);
  return {
    start: formatDate(start),
    end: formatDate(end),
  };
};

const buildNearbyBody = ({ contentId, contentType, placeType, bounds, rating }) => ({
  contentId,
  contentType,
  filters: [
    {
      id: 'placetype',
      value: [placeType],
    },
    {
      id: 'minRating',
      value: [toMinRatingValue(rating)],
    },
  ],
  boundingBox: {
    northEastCorner: {
      latitude: bounds.ne.lat,
      longitude: bounds.ne.lng,
    },
    southWestCorner: {
      latitude: bounds.sw.lat,
      longitude: bounds.sw.lng,
    },
  },
});

const requestListNearbyRaw = async (body) => {
  const { data } = await axios.post(
    `https://${HOST}/locations/v2/list-nearby`,
    body,
    {
      params: {
        currency: 'USD',
        units: 'km',
        lang: 'en_US',
      },
      headers,
    },
  );
  return data;
};

const getAnchorLocationId = (payload) => {
  return payload?.data?.AppPresentation_queryNearToALocation?.mapSections?.[0]?.anchor?.saveId?.id || null;
};

const requestListNearby = async ({ type, bounds, rating }) => {
  const selectedContentType = CONTENT_TYPE_MAP[type] || 'restaurant';
  const selectedPlaceType = PLACETYPE_MAP[type] || 'restaurant';
  const staticContentId = 'cc8fc7b8-88ed-47d3-a70e-0de9991f6604';

  // 1) Bootstrap with restaurant to resolve a location anchor id near the current bounding box.
  let bootstrapPayload = null;
  let anchorLocationId = null;
  try {
    bootstrapPayload = await requestListNearbyRaw(buildNearbyBody({
      contentId: staticContentId,
      contentType: 'restaurant',
      placeType: 'restaurant',
      bounds,
      rating: 0,
    }));
    anchorLocationId = getAnchorLocationId(bootstrapPayload);
  } catch (error) {
    console.warn('bootstrap list-nearby failed:', error?.response?.status || error?.message);
  }

  const requestVariants = [
    buildNearbyBody({
      contentId: anchorLocationId || staticContentId,
      contentType: selectedContentType,
      placeType: selectedPlaceType,
      bounds,
      rating,
    }),
    buildNearbyBody({
      contentId: anchorLocationId || staticContentId,
      contentType: 'restaurant',
      placeType: selectedPlaceType,
      bounds,
      rating,
    }),
    buildNearbyBody({
      contentId: staticContentId,
      contentType: selectedContentType,
      placeType: selectedPlaceType,
      bounds,
      rating,
    }),
  ];

  if (bootstrapPayload) {
    const bootstrapPlaces = parseNearbyResponse(bootstrapPayload);
    if (selectedPlaceType === 'restaurant' && bootstrapPlaces.length) {
      return bootstrapPlaces;
    }
  }

  for (const variant of requestVariants) {
    try {
      const payload = await requestListNearbyRaw(variant);
      const parsed = parseNearbyResponse(payload);
      if (parsed.length) return parsed;
    } catch (error) {
      console.warn('variant list-nearby failed:', error?.response?.status || error?.message);
    }
  }

  return [];
};

const requestTypeSpecificV2List = async ({ type, bounds, geoId }) => {
  if (!geoId) return [];
  const { start, end } = getDateOffsets();

  const endpointMap = {
    restaurants: 'restaurants/v2/list',
    hotels: 'hotels/v2/list',
    attractions: 'attractions/v2/list',
  };
  const endpoint = endpointMap[type];
  if (!endpoint) return [];

  const bodyByType = {
    restaurants: {
      geoId: Number(geoId),
      partySize: 2,
      reservationTime: `${start}T20:00`,
      sort: 'POPULARITY',
      sortOrder: 'desc',
      filters: [],
      boundingBox: {
        northEastCorner: {
          latitude: bounds.ne.lat,
          longitude: bounds.ne.lng,
        },
        southWestCorner: {
          latitude: bounds.sw.lat,
          longitude: bounds.sw.lng,
        },
      },
      updateToken: '',
    },
    hotels: {
      geoId: Number(geoId),
      checkIn: start,
      checkOut: end,
      sort: 'PRICE_LOW_TO_HIGH',
      sortOrder: 'asc',
      filters: [],
      rooms: [{ adults: 1, childrenAges: [] }],
      boundingBox: {
        northEastCorner: {
          latitude: bounds.ne.lat,
          longitude: bounds.ne.lng,
        },
        southWestCorner: {
          latitude: bounds.sw.lat,
          longitude: bounds.sw.lng,
        },
      },
      updateToken: '',
    },
    attractions: {
      geoId: Number(geoId),
      startDate: start,
      endDate: end,
      pax: [{ ageBand: 'ADULT', count: 1 }],
      sort: 'TRAVELER_FAVORITE_V2',
      sortOrder: 'asc',
      filters: [],
      boundingBox: {
        northEastCorner: {
          latitude: bounds.ne.lat,
          longitude: bounds.ne.lng,
        },
        southWestCorner: {
          latitude: bounds.sw.lat,
          longitude: bounds.sw.lng,
        },
      },
      updateToken: '',
    },
  };

  try {
    const { data } = await axios.post(
      `https://${HOST}/${endpoint}`,
      bodyByType[type],
      {
        params: {
          currency: 'USD',
          units: 'km',
          lang: 'en_US',
        },
        headers,
      },
    );

    const parsedNearby = parseNearbyResponse(data);
    if (parsedNearby.length) return parsedNearby;
    return deepExtractPlaces(data);
  } catch (error) {
    console.warn(`${endpoint} failed:`, error?.response?.status || error?.message);
    return [];
  }
};

const requestSearch = async ({ query }) => {
  const { data } = await axios.post(
    `https://${HOST}/locations/v2/search`,
    {
      query,
      updateToken: '',
    },
    {
      params: {
        currency: 'USD',
        units: 'km',
        lang: 'en_US',
      },
      headers,
    },
  );

  return deepExtractPlaces(data);
};

export const searchPlaces = async ({ type, bounds, filters, center }) => {
  if (!API_KEY || !bounds?.sw || !bounds?.ne) return [];

  const queryText = (filters.query || '').trim().toLowerCase();
  const ratingFloor = Number(filters.rating) || 0;
  const priceLevel = filters.priceLevel || 'any';
  const distanceBucket = Number(filters.distanceKm) || 0;

  try {
    const sources = [];
    let bootstrapGeoId = null;

    try {
      const bootstrap = await requestListNearbyRaw(buildNearbyBody({
        contentId: 'cc8fc7b8-88ed-47d3-a70e-0de9991f6604',
        contentType: 'restaurant',
        placeType: 'restaurant',
        bounds,
        rating: 0,
      }));
      bootstrapGeoId = getAnchorLocationId(bootstrap);
    } catch (error) {
      // Best-effort only.
    }

    try {
      const nearbyRaw = await requestListNearby({ type, bounds, rating: ratingFloor });
      sources.push(...nearbyRaw);
    } catch (error) {
      console.warn('locations/v2/list-nearby failed:', error?.response?.status || error?.message);
    }

    if (!sources.length && bootstrapGeoId) {
      const typedList = await requestTypeSpecificV2List({ type, bounds, geoId: bootstrapGeoId });
      sources.push(...typedList);
    }

    if (queryText) {
      try {
        const searchRaw = await requestSearch({ query: queryText });
        sources.push(...searchRaw);
      } catch (error) {
        console.warn('locations/v2/search failed:', error?.response?.status || error?.message);
      }
    }

    const normalized = dedupePlaces(
      sources
        .map(normalizePlace)
        .filter(Boolean),
    );

    let filtered = normalized
      .filter((place) => place.rating >= ratingFloor)
      .filter((place) => (priceLevel === 'any' ? true : place.priceLevel === priceLevel))
      .filter((place) => (filters.openNow ? place.isOpenNow : true));

    if (queryText) {
      const textFiltered = filtered.filter((place) => {
        const name = place.name?.toLowerCase() || '';
        const address = place.address?.toLowerCase() || '';
        return name.includes(queryText) || address.includes(queryText);
      });
      if (textFiltered.length) filtered = textFiltered;
    }

    if (distanceBucket && center) {
      filtered = filtered.filter((place) => {
        const distance = haversineKm(center, { lat: place.lat, lng: place.lng });
        return distance !== null && distance <= distanceBucket;
      });
    }

    if (filters.sortBy === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (filters.sortBy === 'reviews') {
      filtered.sort((a, b) => b.reviews - a.reviews);
    }

    return filtered.slice(0, 60);
  } catch (error) {
    console.error('searchPlaces failed:', error?.response?.status || error?.message);
    return [];
  }
};
